import React, { Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LanguageProvider } from "@/context/LanguageContext";
import PageTransition from "@/components/PageTransition";
import ErrorBoundary from "@/components/ErrorBoundary";
import usePageTitle from "@/hooks/usePageTitle";
import useJourneyTracking from "@/hooks/useJourneyTracking";
import FeedbackWidget from "@/components/FeedbackWidget";

const Landing = React.lazy(() => import("@/pages/Landing"));
const Login = React.lazy(() => import("@/pages/Login"));
const Register = React.lazy(() => import("@/pages/Register"));
const Helpers = React.lazy(() => import("@/pages/Helpers"));
const HelperProfile = React.lazy(() => import("@/pages/HelperProfile"));
const Dashboard = React.lazy(() => import("@/pages/Dashboard"));
const Bookings = React.lazy(() => import("@/pages/Bookings"));
const BookingDetail = React.lazy(() => import("@/pages/BookingDetail"));
const Notifications = React.lazy(() => import("@/pages/Notifications"));
const NotificationSettings = React.lazy(() => import("@/pages/NotificationSettings"));
const Availability = React.lazy(() => import("@/pages/Availability"));
const AdminDashboard = React.lazy(() => import("@/pages/AdminDashboard"));
const CareSeekerProfile = React.lazy(() => import("@/pages/CareSeekerProfile"));
const Groups = React.lazy(() => import("@/pages/Groups"));
const GroupDetail = React.lazy(() => import("@/pages/GroupDetail"));
const Jobs = React.lazy(() => import("@/pages/Jobs"));
const CreateJob = React.lazy(() => import("@/pages/CreateJob"));
const JobDetail = React.lazy(() => import("@/pages/JobDetail"));
const Services = React.lazy(() => import("@/pages/Services"));
const CreateServiceListing = React.lazy(() => import("@/pages/CreateServiceListing"));
const ServiceListingDetail = React.lazy(() => import("@/pages/ServiceListingDetail"));
const VerifyEmail = React.lazy(() => import("@/pages/VerifyEmail"));
const ForgotPassword = React.lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = React.lazy(() => import("@/pages/ResetPassword"));
const StripeOnboardingReturn = React.lazy(() => import("@/pages/StripeOnboardingReturn"));
const Economy = React.lazy(() => import("@/pages/Economy"));
const Contact = React.lazy(() => import("@/pages/Contact"));

const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#FAF0E6]">
    <div className="flex flex-col items-center gap-4">
      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#228B22] to-[#F5B800] animate-pulse" />
      <p className="text-[#228B22]/60 text-sm animate-pulse">Loading...</p>
    </div>
  </div>
);

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <LoadingFallback />;
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

function AnimatedRoutes() {
  const location = useLocation();
  usePageTitle();
  useJourneyTracking();
  
  return (
    <Suspense fallback={<LoadingFallback />}>
      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>
          <Route path="/" element={<PageTransition><Landing /></PageTransition>} />
          <Route path="/login" element={<PageTransition><Login /></PageTransition>} />
          <Route path="/register" element={<PageTransition><Register /></PageTransition>} />
          <Route path="/verify-email" element={<PageTransition><VerifyEmail /></PageTransition>} />
          <Route path="/forgot-password" element={<PageTransition><ForgotPassword /></PageTransition>} />
          <Route path="/reset-password" element={<PageTransition><ResetPassword /></PageTransition>} />
          <Route path="/helpers" element={<PageTransition><Helpers /></PageTransition>} />
          <Route path="/helpers/:id" element={<PageTransition><HelperProfile /></PageTransition>} />
          <Route path="/groups" element={<PageTransition><Groups /></PageTransition>} />
          <Route path="/groups/:id" element={<PageTransition><GroupDetail /></PageTransition>} />
          <Route path="/jobs" element={<PageTransition><Jobs /></PageTransition>} />
          <Route path="/jobs/:id" element={<PageTransition><JobDetail /></PageTransition>} />
          <Route path="/contact" element={<PageTransition><Contact /></PageTransition>} />
          <Route path="/services" element={<PageTransition><Services /></PageTransition>} />
          <Route path="/services/:id" element={<PageTransition><ServiceListingDetail /></PageTransition>} />
          <Route
            path="/jobs/new"
            element={
              <ProtectedRoute>
                <PageTransition><CreateJob /></PageTransition>
              </ProtectedRoute>
            }
          />
          <Route
            path="/services/new"
            element={
              <ProtectedRoute>
                <PageTransition><CreateServiceListing /></PageTransition>
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <PageTransition><Dashboard /></PageTransition>
              </ProtectedRoute>
            }
          />
          <Route
            path="/bookings"
            element={
              <ProtectedRoute>
                <PageTransition><Bookings /></PageTransition>
              </ProtectedRoute>
            }
          />
          <Route
            path="/bookings/:id"
            element={
              <ProtectedRoute>
                <PageTransition><BookingDetail /></PageTransition>
              </ProtectedRoute>
            }
          />
          <Route
            path="/notifications"
            element={
              <ProtectedRoute>
                <PageTransition><Notifications /></PageTransition>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/notifications"
            element={
              <ProtectedRoute>
                <PageTransition><NotificationSettings /></PageTransition>
              </ProtectedRoute>
            }
          />
          <Route
            path="/availability"
            element={
              <ProtectedRoute>
                <PageTransition><Availability /></PageTransition>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <PageTransition><AdminDashboard /></PageTransition>
              </ProtectedRoute>
            }
          />
          <Route
            path="/care-seekers/:id"
            element={
              <ProtectedRoute>
                <PageTransition><CareSeekerProfile /></PageTransition>
              </ProtectedRoute>
            }
          />
          <Route
            path="/economy"
            element={
              <ProtectedRoute>
                <PageTransition><Economy /></PageTransition>
              </ProtectedRoute>
            }
          />
          <Route
            path="/stripe/onboarding/return"
            element={
              <ProtectedRoute>
                <PageTransition><StripeOnboardingReturn /></PageTransition>
              </ProtectedRoute>
            }
          />
          <Route
            path="/stripe/onboarding/refresh"
            element={
              <ProtectedRoute>
                <PageTransition><StripeOnboardingReturn /></PageTransition>
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AnimatePresence>
    </Suspense>
  );
}

function App() {
  return (
    <div className="App">
      <LanguageProvider>
        <AuthProvider>
          <BrowserRouter>
            <ErrorBoundary>
              <AnimatedRoutes />
            </ErrorBoundary>
            <FeedbackWidget />
            <Toaster position="top-right" richColors />
          </BrowserRouter>
        </AuthProvider>
      </LanguageProvider>
    </div>
  );
}

export default App;
